/**
 * The focused API service for reading the case list — an RTK Query slice.
 *
 * This is the read-path twin of request-creation's `casesApi`: it speaks the contract
 * (contracts.ts), owns fetch, envelope reading and query-string building, and nothing
 * else. Contracts and user messages live elsewhere (contracts.ts, errors.ts).
 *
 * The endpoint is a **query**, not a mutation: a page of the list is cacheable, safely
 * refetchable, and idempotent, so RTK Query holds it and serves it back. There is no
 * idempotency key and no retry-under-one-key discipline — those exist on the write path to
 * stop a lost response filing a second case, and a read has nothing to double.
 *
 * The continuation token is opaque end to end (PORT-IDEMPOTENCY's read-path mirror): the
 * SPA holds whatever CMS minted and sends it back verbatim to fetch the next page, and
 * neither the SPA nor the API parses it.
 */
import { createApi } from '@reduxjs/toolkit/query/react';
import { fakeBaseQuery } from '@reduxjs/toolkit/query';
import { apiAuthorizationHeaders } from '@/config/apiAuth';
import type { CaseListPage, CaseListQueryParams, LensApiResponse } from './contracts';
import { type ApiFailure, isApiFailure, listFailure } from './errors';
import { clampPageSize } from '../utils/validation';

const CASES_URL = '/api/v1/cases';

/**
 * Reads whichever shape the API answered a failure with.
 *
 * Failures the caller can act on are promoted to a real 4xx carrying RFC 9457
 * `ProblemDetails`; everything else rides a 200 envelope. Both carry the error code, so
 * this collapses the two into one and the caller never has to know which arrived. Prose
 * (`detail`, `title`) is deliberately not read — it is not the contract.
 */
async function readError(response: Response): Promise<{ code?: string }> {
  try {
    const body = (await response.json()) as
      | LensApiResponse<unknown>
      | { code?: string; extensions?: { code?: string } };

    if ('error' in body && body.error !== undefined) {
      return { code: body.error.code };
    }

    if ('code' in body && typeof body.code === 'string') {
      return { code: body.code };
    }

    if ('extensions' in body && typeof body.extensions?.code === 'string') {
      return { code: body.extensions.code };
    }

    return {};
  } catch {
    return {};
  }
}

/**
 * Builds the query string for one page request.
 *
 * `pageSize` is clamped to the shared ceiling here as well as on the server, so the SPA
 * never asks for a page the server would only bound (PORT-PARITY). Only parameters the
 * caller actually set are emitted — a blank token is a first-page request, not a token CMS
 * would reject. The agent scope is never sent: the server resolves it from a trusted
 * identity, and a client-supplied scope would be an IDOR (PORT-DEFENSE).
 */
function buildQueryString(params: CaseListQueryParams): string {
  const search = new URLSearchParams();

  search.set('pageSize', String(clampPageSize(params.pageSize)));

  if (params.continuationToken !== undefined && params.continuationToken.trim() !== '') {
    search.set('continuationToken', params.continuationToken);
  }

  return search.toString();
}

/**
 * Whether a parsed value is a well-formed page rather than merely JSON that parsed.
 *
 * A 200 can carry `null` or a body missing `items`, both of which parse without throwing;
 * letting either through would only defer the failure to the render, where `items.map`
 * throws far from the boundary that should have classified it (PORT-ERROR).
 */
function isCaseListPage(value: unknown): value is CaseListPage {
  if (typeof value !== 'object' || value === null) {
    return false;
  }

  const page = value as Partial<CaseListPage>;
  return Array.isArray(page.items) && typeof page.hasMoreResults === 'boolean';
}

async function fetchPage(params: CaseListQueryParams): Promise<CaseListPage> {
  const authorization = await apiAuthorizationHeaders(() => listFailure('Unauthenticated', 0));

  let response: Response;
  try {
    response = await fetch(`${CASES_URL}?${buildQueryString(params)}`, {
      method: 'GET',
      headers: { ...authorization, Accept: 'application/json' },
    });
  } catch {
    throw listFailure('TransportFailure', 0);
  }

  if (response.ok) {
    // A 200 whose body will not parse or lacks the envelope is a contract failure, not a
    // transport one: classify it as such and keep the real HTTP status, rather than letting
    // the SyntaxError fall to the queryFn catch and be reported as a network drop that never
    // completed (PORT-ERROR).
    let envelope: LensApiResponse<CaseListPage>;
    try {
      envelope = (await response.json()) as LensApiResponse<CaseListPage>;
    } catch {
      throw listFailure('InvalidResponse', response.status);
    }

    if (envelope === null || typeof envelope !== 'object') {
      // A body that parsed to `null` or a non-object is not the envelope; classify it as a
      // contract failure here rather than reading `isSuccess` off it and having the
      // TypeError fall to the queryFn catch, where it would be mislabelled a transport drop
      // that never completed (PORT-ERROR).
      throw listFailure('InvalidResponse', response.status);
    }

    if (envelope.isSuccess) {
      if (!isCaseListPage(envelope.data)) {
        // A success envelope whose page is absent or malformed must not reach rendering.
        throw listFailure('InvalidResponse', response.status);
      }

      return envelope.data;
    }

    // A 200 carrying a failure: the outcome is not something the caller can act on, so it
    // was deliberately not given an error status.
    throw listFailure((envelope.error?.code ?? 'Unknown') as ApiFailure['code'], response.status);
  }

  const { code } = await readError(response);

  throw listFailure(
    (code ?? (response.status === 401 ? 'Unauthenticated' : 'Unknown')) as ApiFailure['code'],
    response.status,
  );
}

/**
 * The case-list API service.
 *
 * `fakeBaseQuery` because the endpoint needs the bespoke envelope reading above — the
 * `LensApiResponse<T>` unwrap and the coded-failure mapping — not a plain URL fetch. The
 * `queryFn` turns a thrown {@link ApiFailure} into the `error` half of the RTK Query
 * result, which is exactly what `.unwrap()` rejects with.
 */
export const caseListApi = createApi({
  reducerPath: 'caseListApi',
  baseQuery: fakeBaseQuery<ApiFailure>(),
  endpoints: (builder) => ({
    listCases: builder.query<CaseListPage, CaseListQueryParams>({
      queryFn: async (params) => {
        try {
          return { data: await fetchPage(params) };
        } catch (error) {
          return { error: isApiFailure(error) ? error : listFailure('TransportFailure', 0) };
        }
      },
    }),
  }),
});

export const { useListCasesQuery } = caseListApi;
